// ResizableEditor.tsx
import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { Resizable } from 'react-resizable';
import 'react-resizable/css/styles.css';
import './resizable-editor.less'; // Ensure this path is correct
import * as monaco from 'monaco-editor';
import { editor, languages, IRange, Position, IDisposable } from 'monaco-editor/esm/vs/editor/editor.api'; // Import necessary types
import { uniqueId } from 'lodash';
import { defaultFontFamily, defaultInputHeight } from '@config/styledVar'; // Confirm path
import { API } from '@config/api'; // Confirm path
import { expandTypeName, getRawCodeTypeName } from '@lib/quick-func'; // Confirm path
import { FunctionParameter } from '@interface/data-query'; // Confirm path
import { ExprMode, NiButton } from '@component/index'; // Confirm path
// const handleImg = require('@static/image/common/editor-handle.png'); // Confirm path or remove

const MCompletionKind = monaco.languages.CompletionItemKind;

// --- Helper function to convert completion kind ---
function convertCompletionKind(res: string): monaco.languages.CompletionItemKind {
  switch (res) {
    case 'Text': return MCompletionKind.Text;
    case 'Method': return MCompletionKind.Method;
    case 'Function': return MCompletionKind.Function;
    case 'Constructor': return MCompletionKind.Constructor;
    case 'Field': return MCompletionKind.Field;
    case 'Variable': return MCompletionKind.Variable;
    case 'Class': return MCompletionKind.Class;
    case 'Interface': return MCompletionKind.Interface;
    case 'Module': return MCompletionKind.Module;
    case 'Property': return MCompletionKind.Property;
    case 'Unit': return MCompletionKind.Unit;
    case 'Value': return MCompletionKind.Value;
    case 'Enum': return MCompletionKind.Enum;
    case 'Keyword': return MCompletionKind.Keyword;
    case 'Snippet': return MCompletionKind.Snippet;
    case 'Color': return MCompletionKind.Color;
    case 'File': return MCompletionKind.File;
    case 'Reference': return MCompletionKind.Reference;
    case 'Folder': return MCompletionKind.Folder;
    case 'EnumMember': return MCompletionKind.EnumMember;
    case 'Constant': return MCompletionKind.Constant;
    case 'Struct': return MCompletionKind.Struct;
    case 'Event': return MCompletionKind.Event;
    case 'Operator': return MCompletionKind.Operator;
    case 'TypeParameter': return MCompletionKind.TypeParameter;
    default: return MCompletionKind.Text;
  }
}

function getMaxEditorHeight() {
  return window.innerHeight * 0.6;
}


// --- Define Themes ---
monaco.editor.defineTheme('kotlinTemplateTheme', {
  base: 'vs',
  inherit: true,
  rules: [
    { token: 'template-outside', foreground: '000000' },
    { token: 'keyword.kt', foreground: '0000FF' },
    { token: 'identifier.kt', foreground: '267F99' },
    { token: 'number.kt', foreground: '09885A' },
    { token: 'string.kt', foreground: 'A31515' },
    { token: 'template.brace', foreground: '0000FF', fontStyle: 'bold' },
    { token: 'template.brace.close', foreground: '0000FF', fontStyle: 'bold' }
  ],
  colors: {}
});

monaco.editor.defineTheme('customJsonTheme', {
  base: 'vs',
  inherit: true,
  rules: [
    { token: 'string.key.json', foreground: 'A31515' },
    { token: 'string.value.json', foreground: '0451A5' },
    { token: 'number.json', foreground: '098658' },
    { token: 'keyword.json', foreground: '0000FF' },
    { token: 'template.expression.start', foreground: '0000FF', fontStyle: 'bold' },
    { token: 'template.expression.end', foreground: '0000FF', fontStyle: 'bold' },
    { token: 'keyword.kt.embedded', foreground: '0000FF' },
    { token: 'identifier.kt.embedded', foreground: '267F99' },
    { token: 'number.kt.embedded', foreground: '09885A' },
    { token: 'string.kt.embedded', foreground: 'A31515' }
  ],
  colors: {}
});

// --- Register Languages and Tokenizers ---
let customJsonRegistered = false;
let kotlinTemplateRegistered = false;

// --- registerCustomJsonLanguage (Corrected keyword handling) ---
function registerCustomJsonLanguage() {
  if (customJsonRegistered) {
    return;
  }
  customJsonRegistered = true;
  monaco.languages.register({ id: 'customJson' });
  monaco.languages.setMonarchTokensProvider('customJson', {
    kotlinKeywords: ['for', 'if', 'else', 'while', 'class', 'fun', 'val', 'var', 'return', 'true', 'false', 'null'],
    tokenizer: {
      root: [{ include: '@jsonCommon' }],
      jsonCommon: [
        [/[\[\]\{\}]/, '@brackets'],
        [/,/, 'delimiter.comma'],
        [/:/, 'delimiter.colon'],
        [/"/, { token: 'string.quote', bracket: '@open', next: '@string' }],
        [/-?\d+(\.\d+)?([eE][+\-]?\d+)?/, 'number.json'],
        [/\b(true|false|null)\b/, 'keyword.json'],
        [/\s+/, '']
      ],
      string: [
        [/\$\{/, { token: 'template.expression.start', bracket: '@open', next: '@kotlinExpression', nextEmbedded: 'kotlin' }],
        [/\\(?:["\\\/bfnrt]|u[0-9a-fA-F]{4})/, 'string.escape'],
        [/"/, { token: 'string.quote', bracket: '@close', next: '@pop' }],
        [/[^\\"$]+/, 'string.value.json'],
        [/\$/, 'string.value.json'],
        [/\\./, 'string.invalid']
      ],
      kotlinExpression: [
        [/\}/, { token: 'template.expression.end', bracket: '@close', next: '@pop', nextEmbedded: '@pop' }],
        [/[a-zA-Z_]\w*/, { cases: { '@kotlinKeywords': 'keyword.kt.embedded', '@default': 'identifier.kt.embedded' } }],
        [/\d+/, 'number.kt.embedded'],
        [/"(?:[^"\\]|\\.)*"/, 'string.kt.embedded'],
        [/\s+/, ''],
        [/./, '']
      ]
    }
  });
  monaco.languages.setLanguageConfiguration('customJson', {
    brackets: [['{', '}'], ['[', ']']],
    autoClosingPairs: [
      { open: '{', close: '}' },
      { open: '[', close: ']' },
      { open: '"', close: '"', notIn: ['string', 'comment'] }
    ],
    surroundingPairs: [
      { open: '{', close: '}' },
      { open: '[', close: ']' },
      { open: '"', close: '"' }
    ]
  });
}

// --- registerKotlinTemplateLanguage (Corrected keyword handling) ---
function registerKotlinTemplateLanguage() {
  if (kotlinTemplateRegistered) {
    return;
  }
  kotlinTemplateRegistered = true;
  monaco.languages.register({ id: 'kotlinTemplate' });
  monaco.languages.setMonarchTokensProvider('kotlinTemplate', {
    keywords: [
      'for', 'if', 'else', 'while', 'class', 'fun', 'val', 'var', 'return', 'true', 'false', 'null',
      'object', 'when', 'is', 'in', 'try', 'catch', 'finally', 'throw', 'super', 'this', 'new', 'import', 'package'
    ],
    tokenizer: {
      root: [
        [/\$\{/, { token: 'template.brace', bracket: '@open', next: '@kotlinTemplate', nextEmbedded: 'kotlin' }],
        [/[^$]+/, 'template-outside'],
        [/\$/, 'template-outside']
      ],
      kotlinTemplate: [
        [/\}/, { token: 'template.brace.close', bracket: '@close', next: '@pop', nextEmbedded: '@pop' }],
        [/[a-zA-Z_]\w*/, { cases: { '@keywords': 'keyword.kt', '@default': 'identifier.kt' } }],
        [/-?\d+(\.\d+)?/, 'number.kt'],
        [/"(?:[^"\\]|\\.)*"/, 'string.kt'],
        [/[{}()\[\]]/, 'brackets.kt'],
        [/[<> T=+\-*/%&|^!~?:;.,]/, 'operators.kt'],
        [/\s+/, 'white'],
        [/./, ''] // Catch-all rule
      ]
    }
  });
  monaco.languages.setLanguageConfiguration('kotlinTemplate', {
    brackets: [['{', '}'], ['[', ']']],
    autoClosingPairs: [
      { open: '{', close: '}' },
      { open: '[', close: ']' },
      { open: '"', close: '"', notIn: ['string', 'comment'] }
    ],
    surroundingPairs: [
      { open: '{', close: '}' },
      { open: '[', close: ']' },
      { open: '"', close: '"' }
    ]
  });
}

// --- Interfaces ---
export interface CompletionContext {
  rawCodeType?: any;
  rawCodeLang?: string;
  rawCodeParams?: FunctionParameter[];
  rawCodeReturn?: any;
  rawCodeReceiver?: any;
  rawCodeMethodName?: string;
  rawCodeExprMode?: ExprMode;
}
interface EditorProps {
  dataId?: string;
  initialValue: string;
  language: 'kotlin' | 'kotlinTemplate' | 'customJson' | string;
  onChange: (value: string) => void;
  onBlur?: (e: any) => void;
  disabled?: boolean;
  completionContext?: CompletionContext;
  onSave?: (e: any) => void;
  onCancel?: (e: any) => void;
  defaultHeight?: number;
}
export interface EditorHandle { focus: () => void; }

const heightMap: { [key: string]: number } = {};

// --- Component ---
const ResizableEditor = React.forwardRef<EditorHandle, EditorProps>(({
  dataId, initialValue, defaultHeight, language, onChange, disabled,
  completionContext, // Keep prop received
  onBlur, onSave, onCancel
}, ref) => {

  const ideId = useMemo(() => uniqueId('resizable-editor-'), []);
  const ideRef = useRef<editor.IStandaloneCodeEditor | null>(null);
  const parentRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState<number>(0);
  const [height, setHeight] = useState<number>(defaultHeight ?? 84);
  const completionProviderRef = useRef<monaco.IDisposable | null>(null);
  const isDraggingRef = useRef<boolean>(false);
  const btnClickRef = useRef<boolean>(false);
  const previousLanguageRef = useRef<string | null>(null);

  // --- Helper to create Kotlin suggestions (with robust API response check) ---
  const createKotlinSuggestions = useCallback(async (
    range: IRange,
    text: string,
    position: Position,
    context?: CompletionContext // Use the context passed at call time
  ): Promise<languages.CompletionItem[]> => {
    if (!context) {
      return [];
    }
    const { rawCodeType, rawCodeParams, rawCodeReturn, rawCodeReceiver, rawCodeMethodName, rawCodeExprMode } = context;
    let apiRawResponse: any = undefined;
    const suggestions: languages.CompletionItem[] = [];
    try {
      apiRawResponse = await API.common.getIdeSuggestions(
        ideId,
        encodeURIComponent(text),
        position.lineNumber,
        position.column,
        'kotlin',
        getRawCodeTypeName(rawCodeType),
        getRawCodeTypeName(rawCodeReturn),
        rawCodeParams && encodeURIComponent(JSON.stringify(rawCodeParams.map((item: FunctionParameter) => ({
          name: item.name,
          type: item.type && expandTypeName(item.type.name),
          multi_value: item.multiValue,
          nullable: item.nullable
        })))),
        getRawCodeTypeName(rawCodeReceiver),
        rawCodeMethodName,
        rawCodeExprMode
      );

      if (Array.isArray(apiRawResponse)) {
        for (const s of apiRawResponse) {
          if (s && typeof s === 'object' && 'label' in s && 'kind' in s) {
            suggestions.push({
              range,
              label: s.label,
              kind: convertCompletionKind(s.kind),
              insertText: s.insertText || s.label,
              filterText: s.filterText,
              detail: s.detail,
              insertTextRules: s.insertTextFormat === 'Snippet'
                ? languages.CompletionItemInsertTextRule.InsertAsSnippet
                : undefined
            });
          }
        }
      }
    } catch (e: any) {
      // Errors are expected if API fails, no need to log excessively in production
      // console.error('[createKotlinSuggestions] Error during fetch/processing:', e);
      // console.error('[createKotlinSuggestions] API response value on error:', apiRawResponse);
    }
    return suggestions;
  }, [ideId]); // Only depends on stable ideId

  // --- Effect to register/update completion providers ---
  // --- Depends only on language and ideId ---
  useEffect(() => {
    if (language === previousLanguageRef.current && completionProviderRef.current) {
      return;
    }
    // console.log(`Completion Provider EFFECT: Language CHANGED from ${previousLanguageRef.current} to ${language}. Re-registering.`); // DEBUG
    if (completionProviderRef.current) {
      completionProviderRef.current.dispose();
      completionProviderRef.current = null;
    }

    // --- Provider for 'kotlin' ---
    if (language === 'kotlin') {
      completionProviderRef.current = monaco.languages.registerCompletionItemProvider('kotlin', {
        triggerCharacters: ['.', '(', '[', '{', '_', '-', ...'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'.split('')],
        provideCompletionItems: async (model, position) => {
          const word = model.getWordUntilPosition(position);
          const range = {
            startLineNumber: position.lineNumber,
            endLineNumber: position.lineNumber,
            startColumn: word?.startColumn ?? position.column,
            endColumn: word?.endColumn ?? position.column
          };
          const suggestions = await createKotlinSuggestions(range, model.getValue(), position, completionContext);
          return { suggestions };
        }
      });
    } else if (language === 'customJson') {
      registerCustomJsonLanguage();
      completionProviderRef.current = monaco.languages.registerCompletionItemProvider('customJson', {
        triggerCharacters: [
          '$', '.', '(', '[', '{', ' ',
          ...'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789_'.split('')
        ],
        provideCompletionItems: async (model, position, contextTrigger) => {
          const textUntilPosition = model.getValueInRange({
            startLineNumber: position.lineNumber, startColumn: 1,
            endLineNumber: position.lineNumber, endColumn: position.column
          });
          const lineContent = model.getLineContent(position.lineNumber);

          if (contextTrigger.triggerKind === monaco.languages.CompletionTriggerKind.TriggerCharacter &&
            contextTrigger.triggerCharacter === '$') {
            let inString = false; let quoteCount = 0;
            for (let i = 0; i < textUntilPosition.length - 1; i++) {
              if (textUntilPosition[i] === '"' && (i === 0 || textUntilPosition[i - 1] !== '\\')) { quoteCount++; }
            }
            inString = quoteCount % 2 !== 0;
            const isImmediatelyInsideExpr = textUntilPosition.length >= 2 && textUntilPosition.endsWith('{');
            if (inString && !isImmediatelyInsideExpr) {
              const range = {
                startLineNumber: position.lineNumber, endLineNumber: position.lineNumber,
                startColumn: position.column - 1, endColumn: position.column
              };
              return {
                suggestions: [{
                  range, label: '${}', kind: MCompletionKind.Snippet,
                  insertText: `${'$'}{$1}`,
                  insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
                  detail: 'Insert template expression'
                }]
              };
            }
            return { suggestions: [] };
          }

          let insideKotlinExpr = false;
          try {
            let openBracePos = -1; let closeBracePos = -1; let searchPos = position.column - 1;
            while (searchPos >= 0) {
              if (lineContent[searchPos] === '}' && (searchPos === 0 || lineContent[searchPos - 1] !== '\\')) {
                if (openBracePos === -1) { closeBracePos = searchPos; }
              } else if (lineContent[searchPos] === '{' && searchPos > 0 && lineContent[searchPos - 1] === '$' &&
                (searchPos === 1 || lineContent[searchPos - 2] !== '\\')) {
                openBracePos = searchPos - 1; break;
              }
              searchPos--;
            }
            if (openBracePos !== -1) {
              let closingBraceFound = false;
              for (let i = position.column - 1; i < lineContent.length; i++) {
                if (lineContent[i] === '}' && (i === 0 || lineContent[i - 1] !== '\\')) {
                  closingBraceFound = true; break;
                }
                if (lineContent[i] === '{' && i > 0 && lineContent[i - 1] === '$' &&
                  (i === 1 || lineContent[i - 2] !== '\\')) {
                  if ((i - 1) !== openBracePos) { break; }
                }
              }
              if (closingBraceFound) { insideKotlinExpr = true; }
            }
          } catch (e) { /* ignore error */ }

          if (insideKotlinExpr) {
            const word = model.getWordUntilPosition(position);
            const range = {
              startLineNumber: position.lineNumber, endLineNumber: position.lineNumber,
              startColumn: word?.startColumn ?? position.column, endColumn: word?.endColumn ?? position.column
            };
            const suggestions = await createKotlinSuggestions(range, model.getValue(), position, completionContext);
            return { suggestions };
          }
          return { suggestions: [] };
        }
      });
    } else if (language === 'kotlinTemplate') {
      registerKotlinTemplateLanguage();
      completionProviderRef.current = monaco.languages.registerCompletionItemProvider('kotlinTemplate', {
        triggerCharacters: ['$', '.', '(', ...'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ_'.split('')],
        provideCompletionItems: async (model, position, contextTrigger) => {

          if (contextTrigger.triggerKind === languages.CompletionTriggerKind.TriggerCharacter && contextTrigger.triggerCharacter === '$') {
            let isOutsideTemplate = false;
            try {
              const lineContent = model.getLineContent(position.lineNumber);
              const tokens = monaco.editor.tokenize(lineContent, 'kotlinTemplate')[0];
              let tokenAtDollarType = '';
              for (let i = tokens.length - 1; i >= 0; i--) {
                const token = tokens[i];
                if (token.offset <= position.column - 1) { tokenAtDollarType = token.type; break; }
              }
              if (tokenAtDollarType.startsWith('template-outside')) { isOutsideTemplate = true; }
            } catch (e) { /* ignore error */ }

            if (isOutsideTemplate) {
              const range = {
                startLineNumber: position.lineNumber, endLineNumber: position.lineNumber,
                startColumn: position.column - 1, endColumn: position.column
              };
              return {
                suggestions: [{
                  range, label: '${}', kind: MCompletionKind.Snippet,
                  insertText: `${'$'}{$1}`,
                  insertTextRules: languages.CompletionItemInsertTextRule.InsertAsSnippet,
                  detail: 'Insert template expression'
                }]
              };
            }
            return { suggestions: [] };
          }

          let isInsideKotlin = false;
          try {
            const lineContent = model.getLineContent(position.lineNumber);
            const tokens = monaco.editor.tokenize(lineContent, 'kotlinTemplate')[0];
            let currentToken = null;
            for (let i = tokens.length - 1; i >= 0; i--) {
              const token = tokens[i];
              if (token.offset <= position.column - 1) { currentToken = token; break; }
            }
            const currentTokenType = currentToken?.type || '';
            if (!currentTokenType.startsWith('template-outside') && currentTokenType !== '' && currentTokenType !== 'white' &&
              !(currentTokenType === 'template.brace.close' && currentToken?.offset === position.column - 1)) {
              isInsideKotlin = true;
            }
          } catch (e) { /* ignore error */ }

          if (isInsideKotlin) {
            const word = model.getWordUntilPosition(position);
            const range = {
              startLineNumber: position.lineNumber, endLineNumber: position.lineNumber,
              startColumn: word?.startColumn ?? position.column, endColumn: word?.endColumn ?? position.column
            };
            const suggestions = await createKotlinSuggestions(range, model.getValue(), position, completionContext);
            return { suggestions: suggestions || [] };
          }

          return { suggestions: [] };
        }
      });
    }

    previousLanguageRef.current = language;
    return () => { if (completionProviderRef.current) { completionProviderRef.current.dispose(); completionProviderRef.current = null; } };
  }, [language, ideId]); // Correct dependencies


  // --- Editor Creation Effect ---
  useEffect(() => {
    let editorInstance: editor.IStandaloneCodeEditor | null = null;
    if (!ideRef.current && parentRef.current) {
      if (language === 'customJson') {
        registerCustomJsonLanguage();
      } else if (language === 'kotlinTemplate') {
        registerKotlinTemplateLanguage();
      }
      const container = document.getElementById(ideId);
      if (container) {
        let currentTheme = 'vs';
        if (language === 'kotlinTemplate') {
          currentTheme = 'kotlinTemplateTheme';
        } else if (language === 'customJson') {
          currentTheme = 'customJsonTheme';
        }
        const initialWidth = parentRef.current.clientWidth;
        setWidth(initialWidth);
        editorInstance = monaco.editor.create(container, {
          language, value: initialValue, theme: currentTheme,
          automaticLayout: false,
          minimap: { enabled: false },
          fontSize: 14, fontFamily: defaultFontFamily,
          padding: { top: 10, bottom: (onSave || onCancel) ? 40 : 10 },
          folding: true, foldingStrategy: 'auto',
          unfoldOnClickAfterEndOfLine: true,
          scrollBeyondLastLine: false,
          overviewRulerLanes: 0, hideCursorInOverviewRuler: true,
          scrollbar: { vertical: 'auto', horizontal: 'auto', handleMouseWheel: true },
          readOnly: disabled,
          lineNumbers: 'off', renderLineHighlight: 'none',
          quickSuggestions: { other: true, comments: false, strings: false }, // Keep other: true
          acceptSuggestionOnEnter: 'on'
        });
        ideRef.current = editorInstance;
        editorInstance.onDidChangeModelContent(() => {
          onChange(editorInstance?.getValue() || '');
        });
        editorInstance.onDidBlurEditorWidget((event: any) => {
          if (btnClickRef.current) { btnClickRef.current = false; return; }
          if (isDraggingRef.current) return;
          onBlur?.(event);
        });
        // editorInstance.onDidScrollChange((e: any) => {
        //   const editorHeight = editorInstance?.getLayoutInfo().height || 0;
        //   const canScrollUp = e.scrollTop > 0;
        //   const canScrollDown = e.scrollTop + editorHeight < e.scrollHeight;
        //   if (e.scrollTop === 0 && e.scrollTopChange < 0 && !canScrollUp) {
        //     window.scrollBy(0, e.scrollTopChange);
        //   } else if (e.scrollTop + editorHeight >= e.scrollHeight && e.scrollTopChange > 0 && !canScrollDown) {
        //     window.scrollBy(0, e.scrollTopChange);
        //   }
        // });
        const initialLines = (initialValue || '').split('\n').length;
        let calculatedHeight = dataId && heightMap[dataId]
          ? heightMap[dataId]
          : Math.max(initialLines * 20 + 20, defaultHeight || 84);
        if (calculatedHeight > getMaxEditorHeight()) {
          calculatedHeight = getMaxEditorHeight();
        }
        setHeight(calculatedHeight);
        editorInstance.layout({ width: initialWidth, height: calculatedHeight });
      }
    }
    return () => {
      if (ideRef.current) { ideRef.current.dispose(); ideRef.current = null; }
      if (completionProviderRef.current) { completionProviderRef.current.dispose(); completionProviderRef.current = null; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ideId]);

  // --- Imperative Handle ---
  React.useImperativeHandle(ref, () => ({ focus: () => { ideRef.current?.focus(); } }));

  // --- Handle External initialValue Changes ---
  useEffect(() => {
    if (ideRef.current && initialValue !== ideRef.current.getValue()) {
      const model = ideRef.current.getModel();
      if (model) {
        ideRef.current.executeEdits('externalUpdate', [{ range: model.getFullModelRange(), text: initialValue || '', forceMoveMarkers: true }]);
      }
    }
  }, [initialValue]);

  // --- Handle Language/Theme Changes After Mount ---
  useEffect(() => {
    const editor = ideRef.current;
    if (editor) {
      const model = editor.getModel();
      if (model && model.getLanguageId() !== language) {
        if (language === 'customJson') {
          registerCustomJsonLanguage();
        } else if (language === 'kotlinTemplate') {
          registerKotlinTemplateLanguage();
        }
        monaco.editor.setModelLanguage(model, language);
      }
      let newTheme = 'vs';
      if (language === 'kotlinTemplate') {
        newTheme = 'kotlinTemplateTheme';
      } else if (language === 'customJson') {
        newTheme = 'customJsonTheme';
      }
      editor.updateOptions({ theme: newTheme });
    }
  }, [language]);

  // --- Resize Handling ---
  useEffect(() => {
    const handleResize = () => {
      if (parentRef.current && ideRef.current) {
        const newWidth = parentRef.current.clientWidth;
        setWidth(newWidth);
        ideRef.current.layout({ height, width: newWidth });
      }
    };
    window.addEventListener('resize', handleResize);
    return () => { window.removeEventListener('resize', handleResize); };
  }, [height]);

  const onResize = useCallback((event: any, { size }: { size: { width: number; height: number } }) => {
    let nextHeight = Math.max(size.height, defaultInputHeight);
    if (nextHeight > getMaxEditorHeight()) {
      nextHeight = getMaxEditorHeight();
    }
    setHeight(nextHeight);
    if (dataId) { heightMap[dataId] = nextHeight; }
    if (parentRef.current && ideRef.current) {
      const currentWidth = parentRef.current.clientWidth;
      setWidth(currentWidth);
      ideRef.current.layout({ width: currentWidth, height: nextHeight });
    }
  }, [dataId, defaultInputHeight]);

  // --- Button Handling ---
  const buttonContainerStyle: React.CSSProperties = { position: 'absolute', right: 20, bottom: 8, zIndex: 10, display: 'flex', gap: 8 };
  const handleSave = useCallback((e: React.MouseEvent) => { e.stopPropagation(); onSave?.(e); }, [onSave]);
  const handleCancel = useCallback((e: React.MouseEvent) => { e.stopPropagation(); onCancel?.(e); }, [onCancel]);
  const handleButtonMouseDown = useCallback(() => { btnClickRef.current = true; }, []);

  // --- Render ---
  return (
    <div ref={parentRef} style={{ position: 'relative' }}>
      <Resizable
        width={width} height={height} onResize={onResize}
        handle={<span className="react-resizable-handle" />} handleSize={[16, 16]} axis="y"
        minConstraints={[width, defaultInputHeight]} maxConstraints={[width, Infinity]}
        onResizeStart={() => { isDraggingRef.current = true; }} onResizeStop={() => { isDraggingRef.current = false; }}
      >
        <div className="monaco-editor-container" style={{ width: `${width}px`, height: `${height}px`, position: 'relative' }}>
          <div id={ideId} className="editor" style={{ width: '100%', height: '100%' }} />
          {(onSave || onCancel) && (
            <div style={buttonContainerStyle}>
              {onCancel && <NiButton size="small" onMouseDown={handleButtonMouseDown} onClick={handleCancel}>Cancel</NiButton>}
              {onSave && <NiButton size="small" onMouseDown={handleButtonMouseDown} onClick={handleSave} type="primary">Save</NiButton>}
            </div>
          )}
        </div>
      </Resizable>
    </div>
  );
});

export default ResizableEditor;
